import moment from "moment";

import { Constants } from "@eagerdog/constants";
import { IScore } from "@eagerdog/interfaces";

const SHOW_CONFIGS = {
  herding: {
    resultPath: "herding",
    timeField: "elapsed_time",
  },
  akc_agility: {
    resultPath: "akc_agility",
    timeField: "search_time",
  },
} as const;

type ShowType = keyof typeof SHOW_CONFIGS;

const calculateNewPlacement = (
  options: {
    showType: ShowType;
    result: IScore;
    scores: IScore[];
    calculateScore: () => number;
    startedAt?: string;
    finishedAt?: string;
    time?: number | string;
  }
): number | undefined => {
  const {
    showType,
    result,
    scores,
    calculateScore,
    startedAt,
    finishedAt,
    time,
  } = options;

  const config = SHOW_CONFIGS[showType];

  if (!config) {
    return undefined;
  }

  let actualTime: number | string | undefined;

  if (startedAt && finishedAt) {
    actualTime = getTimeDifferenceInSeconds(startedAt, finishedAt);
  } else {
    actualTime = time;
  }

  const score = calculateScore();

  if (!actualTime || !score) {
    return undefined;
  }

  const currentPlacement = getNestedValue(result, `${config.resultPath}.placement`);
  const currentResult = getNestedValue(result, config.resultPath);

  // if no placement change
  if (currentPlacement && actualTime === currentResult?.[config.timeField] && score === currentResult?.score) {
    return currentPlacement;
  }

  const _scores = [...scores];

  const currentResultType = getNestedValue(result, `${config.resultPath}.result_type`);

  // to get pre-calculated placement among qualified scores
  if (currentResultType !== Constants.score_result_type.qualified) {
    _scores.push(result);
  }

  const _score = _scores.find((s) => s._id === result._id);

  if (_score) {
    _score[config.resultPath] = {
      result_type: "",
      [config.timeField]: actualTime,
      score,
    };
  }

  _scores.sort((a, b) => {
    const _a = a[config.resultPath];
    const _b = b[config.resultPath];

    if (_b?.score !== _a?.score) {
      return (_b?.score ?? 0) - (_a?.score ?? 0);
    }

    // @ts-expect-error
    return (_a?.[config.timeField] ?? 0) - (_b?.[config.timeField] ?? 0);
  });

  return _score ? _scores.indexOf(_score) + 1 : undefined;
}

const getNestedValue = (result: any, path: string) => {
  return path
    .split(".")
    .reduce((current, key) => current?.[key], result);
}

const getTimeDifferenceInSeconds = (startTime: string, endTime: string): number => {
  const _start = moment(startTime, "HH:mm:ss");
  const _end = moment(endTime, "HH:mm:ss");

  return _end.diff(_start, "seconds");
}

export { calculateNewPlacement }
